import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

export default function AdminMemeBotSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    maxBotsPerWallet: 5,
    commissionBps: 100,
    infraMarginBps: 10,
    tickIntervalMs: 2000,
    modePresetsJson: '',
    jupiterFiltersJson: '',
  });

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/meme-bot/config`, { headers: headers() });
      setForm({
        maxBotsPerWallet: data.maxBotsPerWallet,
        commissionBps: data.commissionBps,
        infraMarginBps: data.infraMarginBps,
        tickIntervalMs: data.tickIntervalMs,
        modePresetsJson: JSON.stringify(data.modePresets || {}, null, 2),
        jupiterFiltersJson: JSON.stringify(data.jupiterFilters || {}, null, 2),
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let modePresets;
      let jupiterFilters;
      try {
        modePresets = JSON.parse(form.modePresetsJson || '{}');
        jupiterFilters = JSON.parse(form.jupiterFiltersJson || '{}');
      } catch {
        throw new Error('Invalid JSON in mode presets or Jupiter filters');
      }
      await axios.put(
        `${API_BASE}/admin/meme-bot/config`,
        {
          maxBotsPerWallet: Number(form.maxBotsPerWallet),
          commissionBps: Number(form.commissionBps),
          infraMarginBps: Number(form.infraMarginBps),
          tickIntervalMs: Number(form.tickIntervalMs),
          modePresets,
          jupiterFilters,
        },
        { headers: headers() }
      );
      alert('Saved.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading meme bot configuration…</div>;

  return (
    <div className="admin-meme-bot-settings" style={{ maxWidth: 900 }}>
      <h2>🤖 Meme bot platform (admin)</h2>
      <p style={{ opacity: 0.85 }}>
        Max bots per wallet, commission (basis points), tick interval, and JSON presets for conservative / moderate / aggressive
        (slippageBps, maxTradeUsdc). Jupiter filter JSON is reserved for Alpha Scan integration.
      </p>
      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
      <form onSubmit={save}>
        <div className="form-group">
          <label>Max bots per wallet</label>
          <input
            type="number"
            min={1}
            max={99}
            value={form.maxBotsPerWallet}
            onChange={(e) => setForm({ ...form, maxBotsPerWallet: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Commission (basis points, e.g. 100 = 1%)</label>
          <input
            type="number"
            min={0}
            max={500}
            value={form.commissionBps}
            onChange={(e) => setForm({ ...form, commissionBps: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Infra margin (bps, added on top for API costs)</label>
          <input
            type="number"
            min={0}
            max={500}
            value={form.infraMarginBps}
            onChange={(e) => setForm({ ...form, infraMarginBps: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Quote tick interval (ms)</label>
          <input
            type="number"
            min={1000}
            max={60000}
            step={500}
            value={form.tickIntervalMs}
            onChange={(e) => setForm({ ...form, tickIntervalMs: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Mode presets (JSON)</label>
          <textarea
            rows={12}
            value={form.modePresetsJson}
            onChange={(e) => setForm({ ...form, modePresetsJson: e.target.value })}
            style={{ width: '100%', fontFamily: 'monospace' }}
          />
        </div>
        <div className="form-group">
          <label>Jupiter / category filters (JSON)</label>
          <textarea
            rows={8}
            value={form.jupiterFiltersJson}
            onChange={(e) => setForm({ ...form, jupiterFiltersJson: e.target.value })}
            style={{ width: '100%', fontFamily: 'monospace' }}
          />
        </div>
        <button type="submit" className="primary-btn" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
